/* ==========================================================================
   Custom Collection Grid
   --------------------------------------------------------------------------
   Behaviour for the custom-collection-grid section. Loaded once (cached asset)
   and scoped per section instance via the section wrapper's [data-section-id].

   Handles:
     • Hover image — loaded on first mouseenter so mobile never fetches it.
     • Add to cart — progressive enhancement. Both single- and multi-variant
       forms POST natively without JS; here we intercept and add via fetch,
       then re-render Arise's cart sections so the header/drawer update live.
       Add happens ONLY on explicit submit (button), never on select change.
   ========================================================================== */
(function () {
  'use strict';

  /* Build the cart-add URL from Shopify.routes.root so it stays correct on a
     locale-prefixed storefront (e.g. /en-za/). Falls back to root. */
  function cartAddUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/+$/, '') + '/cart/add.js';
  }

  /*
   * Add to cart the same way the Arise theme does natively (see product-list.js):
   * ask xCartHelper which sections to re-render, POST the item, then re-render
   * those sections AND open the drawer via Alpine.store('xMiniCart').openCart()
   * (or the cart notification, if enabled). Reusing the theme's own store methods
   * is what keeps the drawer interactive — remove/close all keep working, and it
   * opens as a drawer rather than a broken full-screen panel.
   *
   * If Alpine / the theme cart isn't available (e.g. the store swaps themes),
   * we fall back to a plain add and a cart-page redirect so nothing is lost.
   */
  function ccgCartAdd(variantId, onSuccess, onError) {
    var A = window.Alpine;
    var helper = A && A.store && A.store('xCartHelper');

    if (!helper) {
      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      })
        .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
        .then(function () {
          onSuccess();
          window.location.href = cartAddUrl().replace('/add.js', '');
        })
        .catch(onError);
      return;
    }

    var sectionIds = [];
    try { sectionIds = helper.getSectionsToRender().map(function (s) { return s.id; }); } catch (e) {}

    Promise.resolve(helper.waitForCartUpdate ? helper.waitForCartUpdate() : null)
      .then(function () {
        window.updatingCart = true;
        return fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds })
        });
      })
      .then(function (r) { return r.json(); })
      .then(function (response) {
        window.updatingCart = false;

        if (response && String(response.status) === '422') {
          document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
            detail: { errorMessage: response.description }
          }));
          onError(response.description);
          return;
        }

        if (response.sections) helper.reRenderSections(response.sections);
        document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: response.items } }));

        if (A.store('xQuickView') && A.store('xQuickView').show) A.store('xQuickView').show = false;
        if (A.store('xPopup') && A.store('xPopup').close) A.store('xPopup').close();

        if (A.store('xCartNoti') && A.store('xCartNoti').enable) {
          A.store('xCartNoti').setItem(response);
        } else if (A.store('xMiniCart') && A.store('xMiniCart').openCart) {
          A.store('xMiniCart').openCart();
          document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
        }

        var bubble = document.getElementById('cart-icon-bubble');
        if (bubble) helper.currentItemCount = parseInt(bubble.innerHTML, 10) || helper.currentItemCount;
        document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

        onSuccess();
      })
      .catch(function (e) { window.updatingCart = false; onError(e); });
  }

  function initHover(section) {
    section.querySelectorAll('.ccg-card--has-hover').forEach(function (card) {
      var secondary = card.querySelector('.ccg-card__img--secondary');
      if (!secondary) return;
      card.addEventListener('mouseenter', function () {
        if (secondary.dataset.src) {
          secondary.src = secondary.dataset.src;
          secondary.srcset = secondary.dataset.srcset || '';
          delete secondary.dataset.src;
          delete secondary.dataset.srcset;
        }
      });
    });
  }

  /* One submit handler for both single- and multi-variant forms. The variant
     id comes from [name="id"] (hidden input for single, <select> for multi). */
  function initForms(section) {
    section.querySelectorAll('.ccg-card__form').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        var control = form.querySelector('[name="id"]');
        if (!control || !control.value) return; /* no variant — let native POST/validation handle it */
        e.preventDefault();

        var btn = form.querySelector('button[type="submit"]');
        if (btn && btn.disabled) return;
        var original = btn ? btn.textContent.trim() : '';
        if (btn) {
          btn.disabled = true;
          btn.textContent = 'Adding…';
        }

        ccgCartAdd(
          control.value,
          function () {
            if (!btn) return;
            btn.textContent = 'Added ✓';
            setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 1500);
          },
          function () {
            if (!btn) return;
            btn.textContent = 'Try again';
            setTimeout(function () { btn.disabled = false; btn.textContent = original; }, 2000);
          }
        );
      });
    });
  }

  function initRoot(root) {
    if (!root || root.dataset.ccgReady) return; /* guard against double-init if loaded more than once */
    root.dataset.ccgReady = 'true';
    var section = document.getElementById('shopify-section-' + root.dataset.sectionId)
      || root.closest('.shopify-section')
      || root;
    initHover(section);
    initForms(section);
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('.ccg[data-section-id]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Theme editor: (re-)initialise a section when it is added or reloaded. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
